/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
// 2) Third-party library imports
import { fetchAuthSession } from 'aws-amplify/auth';
import { get, map, toLower } from 'lodash-es';
import { all, call, delay, fork, put, select, takeLatest } from 'redux-saga/effects';

// 3a) Local imports - Constants
import { API_NAME, maxAPIRefetchCount, refetchAPIDelay } from '../../config/config';
import { CUSTOMERS_PAGE, DETAILS_TAB } from '../../config/tableAndPageConstants';
import { FamilyNameAttribute, GivenNameAttribute } from '../../constants/authUserAttributes';

// 3c) Local imports - Project-specific modules / types
import { _queries as queries } from '../../graphql/queries.graphql';
import { Conversation, PageData } from '../common/types';
import { Invoice } from '../invoices/types';
import { Credit } from '../credits/types';
import { Payment } from '../payments/types';
import {
    Customer,
    CustomersActionTypes,
    CustomerSendInformationRequestPayload,
} from './types';

// 3d) Local imports - Utility functions / interfaces
import dayjs from 'src/config/dayjs';
import {
    checkShouldRequestRefetch,
    formatDateToDateObjectUTC,
    getGraphqlQueryString,
    getRegionallyApiResponse,
    getRegionallyGraphqlResponse,
    getRegionConfigFromList,
    getSortFieldsWithCustomFields,
    removeAppliedFiltersForApiRequest,
    checkIfEmailIsValid,
} from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';
import client, { post } from 'src/api';

// 3e) Local imports - Redux actions & selectors
import {
    bulkSaveCustomerUpdateRequestAction,
    bulkSaveCustomerUpdateResponseAction,
    customerAddCommentRequestAction,
    customerConversationsFetchedForOrganisationAction as customerConversationLinesFetchedForOrganisationAction,
    customerCreditsFetchedForOrganisationAction,
    customerInvoicesFetchedForOrganisationAction,
    customerPaymentsFetchedForOrganisationAction,
    customerSendInformationRequestAction,
    customersFetchedForOrganisationAction,
    getChildCustomersErrorAction,
    getChildCustomersRequestAction,
    getChildCustomersSuccessAction,
    getCustomerConversationErrorAction,
    getCustomerConversationRequestAction,
    getCustomerConversationSuccessAction,
    getCustomerCreditsErrorAction,
    getCustomerCreditsRequestAction,
    getCustomerCreditsSuccessAction,
    getCustomerCustomFieldsForCompanyRequestAction,
    getCustomerCustomFieldsForOrganisationRequestAction,
    getCustomerDataErrorAction,
    getCustomerDataRequestAction,
    getCustomerDataSuccessAction,
    getCustomerFirstLetterValuesForOrganisationRequestAction,
    getCustomerFirstLetterValuesRequestAction,
    getCustomerInvoicesErrorAction,
    getCustomerInvoicesRequestAction,
    getCustomerInvoicesSuccessAction,
    getCustomerPaymentsErrorAction,
    getCustomerPaymentsSuccessAction,
    getCustomersErrorAction,
    getCustomersForOrganisationRequestAction,
    getCustomersPageData,
    getCustomersRequestAction,
    getCustomersSuccessAction,
    getOrganisationCustomerConversationRequestAction,
    getOrganisationCustomerCreditsRequestAction,
    getOrganisationCustomerDataRequestAction,
    getOrganisationCustomerInvoicesRequestAction,
    getOrganisationCustomerPaymentsRequestAction,
    organisationCustomerAddCommentRequestAction,
    getParentCustomerDataErrorAction,
    getParentCustomerDataSuccessAction,
    saveCustomerUpdateRequestAction,
    saveCustomerUpdateResponseAction,
    saveOrganisationCustomerUpdateRequestAction,
    setCustomerSelectedIdRequestAction,
    setCustomerSelectedIdSuccessAction,
    setParentCustomerSelectedIdSuccessAction,
    getCustomerPaymentsRequestAction,
    getParentCustomerDataRequestAction,
    setParentCustomerSelectedIdRequestAction,
    getChildCustomersForOrganisationRequestAction,
    customerAssignUserRequestAction,
} from './customerSlice';
import { getCurrentUser, getRegionKeyConfig, getRegionSettingsConfig } from '../auth/authSlice';
import { getOrganisationActionUrl } from '../organisations/organisationSlice';
import { getCustomerUILabel, getCloudImportType } from '../companies/companySlice';

let refetchCount = 0;

/**
 * Function that calls the API for fetching the customer list.
 * @param param0
 */
function* handleGetCustomersRequest({ payload }: any) {
    try {
        // To call async functions, use redux-saga's `call()`.
        const {
            filters,
            atbType,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            usingCustomerWorkflow,
            usingMultipleWorkflow,
            stateOfCustomers
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer',
            true
        );

        const assignedUserValue = cleanFilters.AssignedUserId;

        if (assignedUserValue) {
            if (checkIfEmailIsValid(assignedUserValue)) {
                delete cleanFilters.AssignedUserId;
                cleanFilters.AssignedEmailAddress = assignedUserValue;
            }
        }

        const cloudImportType: string = yield select(getCloudImportType);
        
        const sortObject = getSortFieldsWithCustomFields(sortBy);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CUSTOMERS_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    ATBType: atbType,
                    ...sortObject,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * CUSTOMERS_PAGE.pageSize,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                    UsingCustomerWorkflow: usingCustomerWorkflow,
                    IsMultipleWorkflow: usingMultipleWorkflow,
                    StateOfCustomers: stateOfCustomers
                }
            },
        );

        const { Customers } = get(res.data, 'GetCustomersForCompany');
        const responsePayload = {
            data: Customers,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(Customers.length < pageSize) &&
                    !(pageSize < CUSTOMERS_PAGE.pageSize),
            },
        };

        refetchCount = 0;

        yield put(getCustomersSuccessAction(responsePayload));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getCustomersRequestAction(payload));
        } else {
            const customerLabel: string = yield select(getCustomerUILabel);
            const errorMessage = `Error fetching ${toLower(
                customerLabel
            )} list. Please try again later.`;
            yield put(getCustomersErrorAction([errorMessage]));
        }
    }
}

function* errorHandler(err: any, payload: any) {
    if (err instanceof Error) {
        console.log('Error', err);
    } else {
        console.error('An unknown error occured.', err);
    }

    if (
        refetchCount <= maxAPIRefetchCount &&
        checkShouldRequestRefetch(err)
    ) {
        refetchCount++;
        yield delay(refetchAPIDelay);
        yield put(getCustomersForOrganisationRequestAction({ ...payload, refetchCount }));
    } else {
        const customerLabel: string = yield select(getCustomerUILabel);
        const errorMessage = `Error fetching ${toLower(
            customerLabel
        )} list. Please try again later.`;
        yield put(getCustomersErrorAction([errorMessage]));
    }
}

function* watchCustomersFetchedForOrganisationReceived() {
    yield takeLatest(CustomersActionTypes.CUSTOMERS_FETCHED_FOR_ORGANISATION_RECEIVED, function* (action: any) {
        const { data, pageSize, currentPage } = action.payload;
        const customers: Customer[] = data.OnCustomersFetchedForOrganisation.Customers;

        const responsePayload = {
            data: customers,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(customers.length < pageSize) && !(pageSize < CUSTOMERS_PAGE.pageSize),
            },
        };

        refetchCount = 0;
        yield put(customersFetchedForOrganisationAction({ data, pageSize, currentPage }));
        yield put(getCustomersSuccessAction(responsePayload));
    });
}

//function* watchCustomersFetchedForOrganisationReceived() {
//    yield takeLatest(CustomersActionTypes.CUSTOMERS_FETCHED_FOR_ORGANISATION_RECEIVED, function* (action: any) {
//        const { data, pageSize, currentPage } = action.payload;
//        const customers: Customer[] = data.OnCustomersFetchedForOrganisation.Customers;

//        const responsePayload = {
//            data: customers,
//            pageData: {
//                pageSize: pageSize,
//                currentPage: currentPage,
//                hasNextPage:
//                    !(customers.length < pageSize) &&
//                    !(pageSize < CUSTOMERS_PAGE.pageSize),
//            },
//        };

//        refetchCount = 0;
//        yield put(getCustomersSuccessAction(responsePayload));
//    });
//}

function* watchCustomerInvoicesFetchedForOrganisationReceived() {
    yield takeLatest(CustomersActionTypes.CUSTOMER_INVOICES_FETCHED_FOR_ORGANISATION_RECEIVED, function* (action: any) {
        const { data, pageSize, currentPage } = action.payload;
        const invoices: Invoice[] = data.OnInvoicesFetchedForOrganisation.Invoices;
       
        const responsePayload = {
            data: invoices,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(invoices.length < pageSize)
            },
        };

        yield put(customerInvoicesFetchedForOrganisationAction({ data, pageSize, currentPage }));
        yield put(getCustomerInvoicesSuccessAction(responsePayload));
    });
}

function* watchCustomerCreditsFetchedForOrganisationReceived() {
    yield takeLatest(CustomersActionTypes.CUSTOMER_CREDITS_FETCHED_FOR_ORGANISATION_RECEIVED, function* (action: any) {
        const { data, pageSize, currentPage } = action.payload;
        const credits: Credit[] = data.OnCreditsFetchedForOrganisation.Credits;

        const responsePayload = {
            data: credits,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(credits.length < pageSize)
            },
        };

        yield put(customerCreditsFetchedForOrganisationAction({ data, pageSize, currentPage }));
        yield put(getCustomerCreditsSuccessAction(responsePayload));
    });
}

function* watchCustomerPaymentsFetchedForOrganisationReceived() {
    yield takeLatest(CustomersActionTypes.CUSTOMER_PAYMENTS_FETCHED_FOR_ORGANISATION_RECEIVED, function* (action: any) {
        const { data, pageSize, currentPage } = action.payload;
        const payments: Payment[] = data.OnPaymentsFetchedForOrganisation.Payments;

        const responsePayload = {
            data: payments,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(payments.length < pageSize)
            },
        };

        yield put(customerPaymentsFetchedForOrganisationAction({ data, pageSize, currentPage }));
        yield put(getCustomerPaymentsSuccessAction(responsePayload));
    });
}

function* watchCustomerConversationLinesFetchedForOrganisationReceived() {
    yield takeLatest(CustomersActionTypes.CUSTOMER_CONVERSATION_LINES_FETCHED_FOR_ORGANISATION_RECEIVED, function* (action: any) {
        const { data, pageSize, currentPage } = action.payload;
        const conversationLines: Conversation[] = data.OnConversationLinesFetchedForOrganisation.ConversationLines;

        const responsePayload = {
            data: conversationLines,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(conversationLines.length < pageSize)
            },
        };

        yield put(customerConversationLinesFetchedForOrganisationAction({ data, pageSize, currentPage }));
        yield put(getCustomerConversationSuccessAction(responsePayload));
    });
}

function* handleGetCustomersForOrganisationRequest({ payload: sagaPayload }: any) {
    let { refetchCount, ...payload } = sagaPayload;
    refetchCount = refetchCount || 0;

    try {
        // To call async functions, use redux-saga's `call()`.
        const {
            filters,
            atbType,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            usingCustomerWorkflow,
            usingMultipleWorkflow,
            conversionCurrency,
            organisationCurrencies,
            stateOfCustomers,
            region
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer',
            true
        );
        const cloudImportType: string = yield select(getCloudImportType);
        const sortObject = getSortFieldsWithCustomFields(sortBy);
        
        // Use region from payload to get correct API URL for the specific tab
        let apiUrl: string | undefined;
        if (region) {
            const regionKeyConfig: DynamicObject[] = yield select(getRegionKeyConfig);
            const regionSettingsConfig: DynamicObject[] = yield select(getRegionSettingsConfig);
            const configUsed = getRegionConfigFromList(region, regionKeyConfig, regionSettingsConfig);
            apiUrl = get(configUsed, 'Url');
        } else {
            apiUrl = yield select(getOrganisationActionUrl);
        }

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CUSTOMERS_FOR_ORGANISATION,
            {
                ...cleanFilters,
                ATBType: atbType,
                // SortField: sortBy,
                ...sortObject,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * CUSTOMERS_PAGE.pageSize,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                UsingCustomerWorkflow: usingCustomerWorkflow,
                IsMultipleWorkflow: usingMultipleWorkflow,
                ConversionCurrency: conversionCurrency,
                OrganisationCurrencies: organisationCurrencies,
                StateOfCustomers: stateOfCustomers
            }
        );

        const { Customers } = get(res.data, 'GetCustomersForOrganisation');
        const responsePayload = {
            data: Customers,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(Customers.length < pageSize) &&
                    !(pageSize < CUSTOMERS_PAGE.pageSize),
            },
        };

        refetchCount = 0;

        yield put(getCustomersSuccessAction(responsePayload));
    } catch (err) {
        errorHandler(err, payload);
    }
}

/**
 * Function calling the API for fetching the customer data based on the given id.
 * @param param0
 */
function* handleGetCustomerDataRequest({
    payload: {
        customerId,
        isUsingCloudImportType,
        callback,
    },
}: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(
        customerLabel
    )} details. Please try again later.`;
    const cloudImportType: string = yield select(getCloudImportType);

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CUSTOMER_DETAILS_FOR_COMPANY,
                variables: {
                    CustomerId: customerId,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                }
            },
        );

        const Customer = get(res.data, 'GetCustomerDetailsForCompany');

        if (!Customer) {
            yield put(getCustomerDataErrorAction([errorMessage]));
            return;
        }

        const responsePayload = {
            record: Customer,
        };

        yield put(getCustomerDataSuccessAction(responsePayload));

        if (callback) {
            callback();
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerDataErrorAction([errorMessage]));
    }
}

/**
 * Function calling the API for fetching the customer data based on the given id.
 * @param param0
 */
function* handleGetOrganisationCustomerDataRequest({
    payload: {
        customerId,
        companyId,
        region,
        isUsingCloudImportType,
        conversionCurrency,
        stateOfCustomers
    },
}: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(
        customerLabel
    )} details. Please try again later.`;

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const cloudImportType: string = yield select(getCloudImportType);
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CUSTOMER_DETAILS_FOR_ORGANISATION,
            {
                CompanyId: companyId,
                CustomerId: customerId,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                ConversionCurrency: conversionCurrency,
                CompanyIds: [companyId],
                StateOfCustomers: stateOfCustomers
            }
        );
        
        const Customer = get(res.data, 'GetCustomerDetailsForOrganisation');

        if (Customer) {
            const responsePayload = {
                record: Customer,
            };

            yield put(getCustomerDataSuccessAction(responsePayload));
        } else {
            yield put(getCustomerDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerDataErrorAction([errorMessage]));
    }
}

/**
 * Function that sets the selected customer id for reference.
 * @param param0
 */
function* handleSetCustomerSelectedIdRequest({ payload }: any) {
    const { customerId, callback } = payload;
    yield put(setCustomerSelectedIdSuccessAction(customerId));

    if (callback) {
        callback();
    }
}

/**
 * Function that sets the selected customer id for reference.
 * @param param0
 */
function* handleSetParentCustomerSelectedIdRequest({ payload }: any) {
    const { customerId, callback } = payload;
    yield put(setParentCustomerSelectedIdSuccessAction(customerId));

    if (callback) {
        callback();
    }
}

/**
 * Function calling the API for fetching the parent customer data based on the given id.
 * @param param0
 */
function* handleGetParentCustomerDataRequest({
    payload: {
        customerId,
        companyId,
        isUsingCloudImportType,
        conversionCurrency,
    },
}: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(customerLabel)} details. Please try again later.`;
    const cloudImportType: string = yield select(getCloudImportType);

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CUSTOMER_DETAILS_FOR_COMPANY,
                variables: {
                    CustomerId: customerId,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                }
            }
        );

        const Customer = get(res.data, 'GetCustomerDetailsForCompany');

        if (Customer) {
            const responsePayload = {
                record: Customer,
            };
            
            yield put(getParentCustomerDataSuccessAction(responsePayload));
        } else {
            yield put(getParentCustomerDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getParentCustomerDataErrorAction([errorMessage]));
    }
}

/**
 * Function that calls the API for fetching the customer children list.
 * @param param0
 */
function* handleGetChildCustomersRequest({ payload }: any) {
    try {
        // To call async functions, use redux-saga's `call()`.
        const {
            filters,
            atbType,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            usingCustomerWorkflow,
            usingMultipleWorkflow,
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer',
            true,
            'Customer',
            true
        );

        const cloudImportType: string = yield select(getCloudImportType);
        const sortObject = getSortFieldsWithCustomFields(sortBy);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CUSTOMERS_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    ATBType: atbType,
                    ...sortObject,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * CUSTOMERS_PAGE.pageSize,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                    UsingCustomerWorkflow: usingCustomerWorkflow,
                    IsMultipleWorkflow: usingMultipleWorkflow,
                }
            },
        );

        const { Customers } = get(res.data, 'GetCustomersForCompany');
        const responsePayload = {
            data: Customers,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(Customers.length < pageSize) &&
                    !(pageSize < CUSTOMERS_PAGE.pageSize),
            },
        };

        refetchCount = 0;
        yield put(getChildCustomersSuccessAction(responsePayload));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getChildCustomersRequestAction(payload));
        } else {
            const customerLabel: string = yield select(getCustomerUILabel);
            const errorMessage = `Error fetching ${toLower(
                customerLabel
            )} list. Please try again later.`;
            yield put(getChildCustomersErrorAction([errorMessage]));
        }
    }
}

function* handleGetChildCustomersForOrganisationRequest({ payload: sagaPayload }: any) {
    let { refetchCount, ...payload } = sagaPayload;
    refetchCount = refetchCount || 0;

    try {
        // To call async functions, use redux-saga's `call()`.
        const {
            filters,
            atbType,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            usingCustomerWorkflow,
            usingMultipleWorkflow,
            conversionCurrency,
            organisationCurrencies,
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer',
            true
        );
        const cloudImportType: string = yield select(getCloudImportType);
        const sortObject = getSortFieldsWithCustomFields(sortBy);
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CUSTOMERS_FOR_ORGANISATION,
            {
                ...cleanFilters,
                ATBType: atbType,
                // SortField: sortBy,
                ...sortObject,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * CUSTOMERS_PAGE.pageSize,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                UsingCustomerWorkflow: usingCustomerWorkflow,
                IsMultipleWorkflow: usingMultipleWorkflow,
                ConversionCurrency: conversionCurrency,
                OrganisationCurrencies: organisationCurrencies,
                ExcludeParent: true
            }
        );

        const { Customers } = get(res.data, 'GetCustomersForOrganisation');
        const responsePayload = {
            data: Customers,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(Customers.length < pageSize) &&
                    !(pageSize < CUSTOMERS_PAGE.pageSize),
            },
        };

        refetchCount = 0;
        yield put(getCustomersSuccessAction(responsePayload));
    } catch (err) {
        errorHandler(err, payload);
    }
}

/**
 * Function that gets the invoices list for a certain customer.
 * @param param0
 */
function* handleGetCustomerInvoicesRequest({ payload }: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(customerLabel)}'s invoice list. Please try again later.`;

    try {
        const {
            filters,
            invoiceState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer'
        );

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_INVOICES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    InvoiceState: invoiceState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
                }
            }
        );

        const { Invoices } = get(res.data, 'GetInvoicesForCompany');

        if (Invoices) {
            const responsePayload = {
                data: Invoices,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(Invoices.length < pageSize),
                },
            };

            yield put(getCustomerInvoicesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerInvoicesErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the invoices list for a certain customer.
 * @param param0
 */
function* handleGetOrganisationCustomerInvoicesRequest({
    payload: sagaPayload
}: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(customerLabel)}'s invoice list. Please try again later.`;
    const { payload, region } = sagaPayload;

    try {    
        const {
            filters,
            invoiceState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            conversionCurrency,
            organisationCurrencies
        } = payload;
        
        const currentSession: DynamicObject = yield call(fetchAuthSession);
        const accessToken = get(currentSession, 'tokens.accessToken');

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer'
        );
        let apiUrl: string | undefined = undefined;

        if (region) {
            const regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );
            const regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );
            const configUsed = getRegionConfigFromList(
                region,
                regionKeyConfig,
                regionSettingsConfig
            );
            apiUrl = get(configUsed, 'Url');
        }

        let res: DynamicObject;
        
        const variables = {
            ...cleanFilters,
            InvoiceState: invoiceState,
            SortField: sortBy,
            Ascending: sortAscending,
            PageSize: pageSize,
            Skip: currentPage * DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
            OrganisationCurrencies: organisationCurrencies,
            ConversionCurrency: conversionCurrency
        };

        if (apiUrl) {
            const response: Response = yield fetch(
                `${apiUrl}/graphql`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify({
                        query: getGraphqlQueryString(queries.GET_INVOICES_FOR_ORGANISATION),
                        variables,
                    }),
                }
            );
            res = yield response.json();
        } else {
            res = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_INVOICES_FOR_ORGANISATION,
                    variables: variables
                }
            );
        }
        
        const { Invoices } = get(res.data, 'GetInvoicesForOrganisation');
        if (Invoices) {
            const responsePayload = {
                data: Invoices,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(Invoices.length < pageSize),
                },
            };

            yield put(getCustomerInvoicesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerInvoicesErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the credits list for a certain customer.
 * @param param0
 */
function* handleGetCustomerCreditsRequest({ payload }: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(customerLabel)}'s credit list. Please try again later.`;

    try {
        const {
            filters,
            creditState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer'
        );

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CREDITS_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    CreditState: creditState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
                }
            },
        );

        const { Credits } = get(res.data, 'GetCreditsForCompany');

        if (Credits) {
            const responsePayload = {
                data: Credits,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(Credits.length < pageSize),
                },
            };

            yield put(getCustomerCreditsSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerCreditsErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the credits list for a certain customer.
 * @param param0
 */
function* handleGetOrganisationCustomerCreditsRequest({
    payload: sagaPayload
}: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(customerLabel)}'s credit list. Please try again later.`;
    const { payload, region } = sagaPayload;

    try {
        const {
            filters,
            creditState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            conversionCurrency,
            organisationCurrencies,
            isUsingCloudImportType,
        } = payload;

        const currentSession: DynamicObject = yield call(fetchAuthSession);
        const accessToken = get(currentSession, 'tokens.accessToken');

        //yield fork(
        //    (subscriptionQuery, onNext, onError) =>
        //        SubscribeToUpdates<CreditsFetchedData>(
        //            subscriptionQuery,
        //            { IsCloudImportType: isUsingCloudImportType },  // Pass the variables here
        //            onNext,
        //            onError,
        //            accessToken
        //        ),
        //    subscriptions.ON_CREDITS_FETCHED_FOR_ORGANISATION,
        //    CustomerCreditsFetchedForOrganisationReceived({ pageSize, currentPage }),
        //    function handleError(error: any) {
        //        console.error('Subscription error:', JSON.stringify(error, null, 2));
        //    }
        //);

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer'
        );
        let apiUrl: string | undefined = undefined;
        
        if (region) {
            const regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );
            const regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );
            const configUsed = getRegionConfigFromList(
                region,
                regionKeyConfig,
                regionSettingsConfig
            );
            apiUrl = get(configUsed, 'Url');
        }

        let res: DynamicObject;
        
        const variables = {
            ...cleanFilters,
            CreditState: creditState,
            SortField: sortBy,
            Ascending: sortAscending,
            PageSize: pageSize,
            Skip: currentPage * DETAILS_TAB.CREDIT_LIST_COMPLETE.pageSize,
            ConversionCurrency: conversionCurrency,
            OrganisationCurrencies: organisationCurrencies,
        };

        if (apiUrl) {
            const response: Response = yield fetch(
                `${apiUrl}/graphql`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify({
                        query: getGraphqlQueryString(queries.GET_CREDITS_FOR_ORGANISATION),
                        variables,
                    }),
                }
            );

            res = yield response.json();
        } else {
            res = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_CREDITS_FOR_ORGANISATION,
                    variables: variables
                }
            );
        }

        const { Credits } = get(res.data, 'GetCreditsForOrganisation');

        if (Credits) {
            const responsePayload = {
                data: Credits,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(Credits.length < pageSize),
                },
            };

            yield put(getCustomerCreditsSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerCreditsErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the payments list for a certain customer.
 * @param param0
 */
function* handleGetCustomerPaymentsRequest({ payload }: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(customerLabel)}'s payment list. Please try again later.`;

    try {
        const {
            filters,
            paymentState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer'
        );
        const cloudImportType: string = yield select(getCloudImportType);

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENTS_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    PaymentState: paymentState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.PAYMENT_LIST_COMPLETE.pageSize,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                }
            }
        );

        const { Payments } = get(res.data, 'GetPaymentsForCompany');

        if (Payments) {
            const responsePayload = {
                data: Payments,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage: !(Payments.length < pageSize),
                },
            };

            yield put(getCustomerPaymentsSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerPaymentsErrorAction([errorMessage]));
    }
}

/**
 * Function that gets the payments list for a certain customer.
 * @param param0
 */
function* handleGetOrganisationCustomerPaymentsRequest({
    payload: sagaPayload
}: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(customerLabel)}'s payment list. Please try again later.`;
    const { payload, region } = sagaPayload;

    try {
        const {
            filters,
            paymentState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            conversionCurrency,
            organisationCurrencies
        } = payload;

        const currentSession: DynamicObject = yield call(fetchAuthSession);
        const accessToken = get(currentSession, 'tokens.accessToken');

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer'
        );

        let apiUrl: string | undefined = undefined;

        if (region) {
            const regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );
            const regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );
            const configUsed = getRegionConfigFromList(
                region,
                regionKeyConfig,
                regionSettingsConfig
            );
            apiUrl = get(configUsed, 'Url');
        }

        let res: DynamicObject;
        const cloudImportType: string = yield select(getCloudImportType);
        
        const variables = {
            ...cleanFilters,
            PaymentState: paymentState,
            SortField: sortBy,
            Ascending: sortAscending,
            PageSize: pageSize,
            Skip: currentPage * DETAILS_TAB.PAYMENT_LIST_COMPLETE.pageSize,
            IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
            OrganisationCurrencies: organisationCurrencies,
            ConversionCurrency: conversionCurrency,
        };

        if (apiUrl) {
            const response: Response = yield fetch(
                `${apiUrl}/graphql`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify({
                        query: getGraphqlQueryString(queries.GET_PAYMENTS_FOR_ORGANISATION),
                        variables,
                    }),
                }
            );

            res = yield response.json();
        } else {
            res = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_PAYMENTS_FOR_ORGANISATION,
                    variables: variables
                }
            );
        }

        const { Payments } = get(res.data, 'GetPaymentsForOrganisation');

         if (Payments) {
             const responsePayload = {
                 data: Payments,
                 pageData: {
                     pageSize: pageSize,
                     currentPage: currentPage,
                     hasNextPage: !(Payments.length < pageSize),
                 },
             };

             yield put(getCustomerPaymentsSuccessAction(responsePayload));
         }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerPaymentsErrorAction([errorMessage]));
    }
}

/**
 * Function responsible for updating the VIP status of a single customer.
 * @param param0
 */
function* handleSaveCustomerVIPUpdateRequest({ payload: sagaPayload }: any) {
    const { customerId, customerObject, callback } = sagaPayload;

    try {
        const payload = {
            CustomerId: customerId,
            Customer: customerObject,
            ClearIsVipExpiryDate: customerObject.ClearIsVipExpiryDate
        };

        yield call(post, {
            apiName: API_NAME,
            path: '/customer/save',
            options: {
                body: payload
            }
        });

        yield put(saveCustomerUpdateResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        yield put(saveCustomerUpdateResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function responsible for updating the VIP status of a single customer.
 * @param param0
 */
function* handleSaveOrganisationCustomerVIPUpdateRequest({ payload: sagaPayload }: any) {
    const { payload, region } = sagaPayload;
    const { customerId, companyId, customerObject, callback } = payload;

    try {
        const postPayload = {
            CustomerId: customerId,
            CompanyId: companyId,
            Customer: customerObject,
        };

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/customer/save',
            'POST',
            postPayload
        );
        
        yield put(saveCustomerUpdateResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        yield put(saveCustomerUpdateResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for updating the VIP status of multiple customers.
 * @param param0
 */
function* handleBulkSaveCustomersVIPUpdateRequest({
    payload: sagaPayload,
}: any) {
    const { filter, customerIds, excludeCustomers, customerObject, stateOfCustomers, callback } =
        sagaPayload;
    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'customer'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        CustomerManagementFilter: {
            ...cleanFilters,
            CustomerIds: customerIds,
            ExcludeCustomers: excludeCustomers,
            StateOfCustomers: stateOfCustomers
        },
        Customer: customerObject,
        ClearIsVipExpiryDate: customerObject.ClearIsVipExpiryDate
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/customer/batch/save',
            options: {
                body: payload
            }
        });

        yield put(bulkSaveCustomerUpdateResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        yield put(bulkSaveCustomerUpdateResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for fetching the conversation list of a given customer.
 * @param param0
 */
function* handleGetCustomerConversationRequest({ payload }: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(customerLabel)}'s conversation list. Please try again later.`;

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer',
            true
        );

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CONVERSATION_LINES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                }
            }
        );

        const { ConversationLines } = get(
            res.data,
            'GetConversationLinesForCompany'
        );

        const Conversation = ConversationLines;

        if (Conversation) {
            const responsePayload = {
                data: Conversation,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Conversation.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CONVERSATION_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getCustomerConversationSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerConversationErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the conversation list of a given customer.
 * @param param0
 */
function* handleGetOrganisationCustomerConversationRequest({
    payload: sagaPayload
}: any) {
    const customerLabel: string = yield select(getCustomerUILabel);
    const errorMessage = `Error fetching ${toLower(customerLabel)}'s conversation list. Please try again later.`;
    const { payload, region, companyIds} = sagaPayload;

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer',
            true
        );
        
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CONVERSATION_LINES_FOR_ORGANISATION,
            {
                ...cleanFilters,
                CompanyIds: companyIds,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
            }
        );

        const { ConversationLines } = get(res.data, 'GetConversationLinesForOrganisation');
        const Conversation = ConversationLines;

         if (Conversation) {
             const responsePayload = {
                 data: Conversation,
                 pageData: {
                     pageSize,
                     currentPage: currentPage,
                     hasNextPage:
                         !(Conversation.length < pageSize) &&
                         !(
                             pageSize <
                             DETAILS_TAB.CONVERSATION_TIMELINE.pageSize
                         ),
                 },
             };

             yield put(getCustomerConversationSuccessAction(responsePayload));
         }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getCustomerConversationErrorAction([errorMessage]));
    }
}

/**
 * Function for adding comment to the customer's conversation list.
 * @param param0
 */
function* handleCustomerAddCommentRequest({ payload: sagaPayload }: any) {
    const { filter, customerIds, excludeCustomers, comment, callback } =
        sagaPayload;

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'customer'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        CustomerManagementFilter: {
            ...cleanFilters,
            CustomerIds: customerIds,
            ExcludeCustomers: excludeCustomers,
        },
        Comment: comment,
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/conversation/save/customercomment',
            options: {
                body: payload
            }
        });

        if (callback) {
            let RefetchList = true;

            if (customerIds.length === 1 && excludeCustomers === false) {
                RefetchList = false;
                const currentUser: DynamicObject = yield select(getCurrentUser);

                const customersUpdated: DynamicObject[] = map(
                    customerIds,
                    (uId: string) => {
                        return {
                            Id: uId,
                            ConversationLine: {
                                Content: comment,
                                CreatedDateTime: formatDateToDateObjectUTC(
                                    dayjs(),
                                    undefined,
                                    true
                                ),
                                User: {
                                    GivenName: get(
                                        currentUser,
                                        GivenNameAttribute
                                    ),
                                    FamilyName: get(
                                        currentUser,
                                        FamilyNameAttribute
                                    ),
                                },
                            },
                        };
                    }
                );

                const pageData: PageData = yield select(getCustomersPageData);

                yield put(
                    getCustomersSuccessAction({
                        data: customersUpdated as Customer[],
                        pageData,
                        mergeData: true,
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for adding comment to the customer's conversation list.
 * @param param0
 */
function* handleOrganisationCustomerAddCommentRequest({ payload: sagaPayload }: any) {
    const { payload, region, companyIds } = sagaPayload;
    const { filter, customerIds, excludeCustomers, comment, callback } =
        payload;

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'customer'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const postPayload = {
        CustomerManagementFilter: {
            ...cleanFilters,
            CustomerIds: customerIds,
            CompanyIds: companyIds,
            ExcludeCustomers: excludeCustomers,
        },
        Comment: comment,
    };

    try {
        let apiUrl: string | undefined = undefined;

        if (region) {
            const regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );
            const regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );
            const configUsed = getRegionConfigFromList(
                region,
                regionKeyConfig,
                regionSettingsConfig
            );
            apiUrl = get(configUsed, 'Url');
        }

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = currentSession.tokens.accessToken;

            yield fetch(
                `${apiUrl}/conversation/save/customercomment`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify(postPayload),
                }
            );
        } else {
            yield call(post, {
                apiName: API_NAME,
                path: '/conversation/save/customercomment',
                options: {
                    body: postPayload
                }
            });
        }
        
        if (callback) {
            let RefetchList = true;

            if (customerIds.length === 1 && excludeCustomers === false) {
                RefetchList = false;
                const currentUser: DynamicObject = yield select(getCurrentUser);

                const customersUpdated: DynamicObject[] = map(
                    customerIds,
                    (uId: string) => {
                        return {
                            Id: uId,
                            ConversationLine: {
                                Content: comment,
                                CreatedDateTime: formatDateToDateObjectUTC(
                                    dayjs(),
                                    undefined,
                                    true
                                ),
                                User: {
                                    GivenName: get(
                                        currentUser,
                                        GivenNameAttribute
                                    ),
                                    FamilyName: get(
                                        currentUser,
                                        FamilyNameAttribute
                                    ),
                                },
                            },
                        };
                    }
                );

                const pageData: PageData = yield select(getCustomersPageData);

                yield put(
                    getCustomersSuccessAction({
                        data: customersUpdated as Customer[],
                        pageData,
                        mergeData: true,
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for send information to the customers
 * @param param0
 */
function* handleCustomerSendInformationRequest({ payload: sagaPayload }: any) {
    const { 
        filter, 
        customerIds, 
        excludeCustomers, 
        TemplateName, 
        callback,
        isCustomerPortalSendingStatement,
        alternativesEmail,
        invoiceIds,
        excludeInvoices
    } = sagaPayload as CustomerSendInformationRequestPayload;

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'customer'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        CustomerManagementFilter: {
            ...cleanFilters,
            CustomerIds: customerIds,
            ExcludeCustomers: excludeCustomers,
        },
        TemplateName: TemplateName,
        IsCustomerPortalSendingStatement: isCustomerPortalSendingStatement,
        AlternativesEmail: alternativesEmail,
        InvoiceIds: invoiceIds,
        ExcludeInvoices: excludeInvoices
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/task/action/manual-communication/send',
            options: {
                body: payload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the customer custom fields for a company.
 * @param param0
 */
function* handleGetCustomerCustomFieldsForCompanyRequest({ payload }: any) {
    const { 
        CompanyId, 
        FieldName,
        callback
    } = payload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CUSTOMER_CUSTOM_FIELDS_FOR_COMPANY,
                variables: {
                    CompanyId: CompanyId,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );
        
        const CustomFieldValues  = get(res.data, 'GetCustomerCustomFieldsForCompany');
        
        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the customer custom fields for a company.
 * @param param0
 */
function* handleGetCustomerCustomFieldsForOrganisationRequest({ payload }: any) {
    const errorMessage = '';
    const { 
            CompanyIds, 
            FieldName,
            callback
        } = payload;
    try {
        const errorMessage =
        'Error fetching custom field values. Please try again later.';
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CUSTOMER_CUSTOM_FIELDS_FOR_ORGANISATION,
                variables: {
                    CompanyIds: CompanyIds,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );
        
        const CustomFieldValues  = get(res.data, 'GetCustomerCustomFieldsForOrganisation');
        
        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }

    } catch (err) {
        if (callback) callback([]);
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the customer custom fields for a company.
 * @param param0
 */
function* handleGetCustomerFirstLetterValuesRequest({ payload }: any) {
    const {
        companyId,
        callback
    } = payload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CUSTOMER_FIRST_LETTER_VALUES,
                variables: {
                    CompanyId: companyId,
                }
            }
        );

        if (callback) {
            callback({
                IsSuccess: true,
                Values: get(res.data, 'GetCustomerFirstLetterValues.Values')
            });
        } 
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
    }
}

/**
 * Function for getting the customer custom fields for a organisation.
 * @param param0
 */
function* getCustomerFirstLetterValuesForOrganisationRequest({ payload }: any) {
    const {
        companyIds,
        callback
    } = payload;

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CUSTOMER_FIRST_LETTER_VALUES_FOR_ORGANISATION,
            {
                CompanyIds: companyIds
            }
        );
        
        if (callback) {
            callback({
                IsSuccess: true,
                Values: get(res.data, 'GetCustomerFirstLetterValuesForOrganisation.Values')
            });
        } else {
            throw new Error('Get Customer First Letter Values For Organisation Failed.')
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called for assign user to customers.
 * @param param0
 */
function* handleCustomerAssignUserRequest({
    payload: sagaPayload,
}: any) {
    const { filter, customerIds, excludeCustomers, userObject, callback, companyId } = sagaPayload;
    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'customer'
    );

    const payload = {
        CustomerManagementFilter: {
            ...cleanFilters,
            CustomerIds: customerIds,
            ExcludeCustomers: excludeCustomers,
            CompanyId: companyId
        },
        ...userObject,
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/customer/action/assign-user',
            options: {
                body: payload
            }
        });
        const res: DynamicObject = yield body.json();
        
        const successMessages: any = [];
        successMessages.push(
            `Customer${payload.CustomerManagementFilter.CustomerIds.length > 1 || payload.CustomerManagementFilter.ExcludeCustomers ? 's have' : ' has'
            } been assigned successfully!`
        );

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.data) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetCustomersRequest() {
    yield takeLatest(
        getCustomersRequestAction.type,
        handleGetCustomersRequest
    );
}

function* watchGetCustomersForOrganisationRequest() {
    yield takeLatest(
        getCustomersForOrganisationRequestAction.type,
        handleGetCustomersForOrganisationRequest
    );
}

function* watchGetCustomerDataRequest() {
    yield takeLatest(
        getCustomerDataRequestAction.type,
        handleGetCustomerDataRequest
    );
}

function* watchGetOrganisationCustomerDataRequest() {
    yield takeLatest(
        getOrganisationCustomerDataRequestAction.type,
        handleGetOrganisationCustomerDataRequest
    );
}

function* watchSetCustomerSelectedIdRequest() {
    yield takeLatest(
        setCustomerSelectedIdRequestAction.type,
        handleSetCustomerSelectedIdRequest
    );
}

function* watchGetChildCustomersRequest() {
    yield takeLatest(
        getChildCustomersRequestAction.type,
        handleGetChildCustomersRequest
    );
}

function* watchGetParentCustomerDataRequest() {
    yield takeLatest(
        getParentCustomerDataRequestAction.type,
        handleGetParentCustomerDataRequest
    );
}

function* watchSetParentCustomerSelectedIdRequest() {
    yield takeLatest(
        setParentCustomerSelectedIdRequestAction.type,
        handleSetParentCustomerSelectedIdRequest
    );
}

function* watchGetChildCustomersForOrganisationRequest() {
    yield takeLatest(
        getChildCustomersForOrganisationRequestAction.type,
        handleGetChildCustomersForOrganisationRequest
    );
}

function* watchGetCustomerInvoicesRequest() {
    yield takeLatest(
        getCustomerInvoicesRequestAction.type,
        handleGetCustomerInvoicesRequest
    );
}

function* watchGetOrganisationCustomerInvoicesRequest() {
    yield takeLatest(
        getOrganisationCustomerInvoicesRequestAction.type,
        handleGetOrganisationCustomerInvoicesRequest
    );
}

function* watchGetCustomerCreditsRequest() {
    yield takeLatest(
        getCustomerCreditsRequestAction.type,
        handleGetCustomerCreditsRequest
    );
}

function* watchGetOrganisationCustomerCreditsRequest() {
    yield takeLatest(
        getOrganisationCustomerCreditsRequestAction.type,
        handleGetOrganisationCustomerCreditsRequest
    );
}

function* watchGetCustomerPaymentsRequest() {
    yield takeLatest(
        getCustomerPaymentsRequestAction.type,
        handleGetCustomerPaymentsRequest
    );
}

function* watchGetOrganisationCustomerPaymentsRequest() {
    yield takeLatest(
        getOrganisationCustomerPaymentsRequestAction.type,
        handleGetOrganisationCustomerPaymentsRequest
    );
}

function* watchSaveCustomerVIPUpdateRequest() {
    yield takeLatest(
        saveCustomerUpdateRequestAction.type,
        handleSaveCustomerVIPUpdateRequest
    );
}

function* watchSaveOrganisationCustomerVIPUpdateRequest() {
    yield takeLatest(
        saveOrganisationCustomerUpdateRequestAction.type,
        handleSaveOrganisationCustomerVIPUpdateRequest
    );
}

function* watchBulkSaveCustomersVIPUpdateRequest() {
    yield takeLatest(
        bulkSaveCustomerUpdateRequestAction.type,
        handleBulkSaveCustomersVIPUpdateRequest
    );
}

function* watchGetCustomerConversationRequest() {
    yield takeLatest(
        getCustomerConversationRequestAction.type,
        handleGetCustomerConversationRequest
    );
}

function* watchGetOrganisationCustomerConversationRequest() {
    yield takeLatest(
        getOrganisationCustomerConversationRequestAction.type,
        handleGetOrganisationCustomerConversationRequest
    );
}

function* watchCustomerAddCommentRequest() {
    yield takeLatest(
        customerAddCommentRequestAction.type,
        handleCustomerAddCommentRequest
    );
}

function* watchOrganisationCustomerAddCommentRequest() {
    yield takeLatest(
        organisationCustomerAddCommentRequestAction.type,
        handleOrganisationCustomerAddCommentRequest
    );
}

function* watchCustomerSendInformationRequest() {
    yield takeLatest(
        customerSendInformationRequestAction.type,
        handleCustomerSendInformationRequest
    );
}

function* watchGetCustomerCustomFieldsForCompanyRequest() {
    yield takeLatest(
        getCustomerCustomFieldsForCompanyRequestAction.type,
        handleGetCustomerCustomFieldsForCompanyRequest
    );
}

function* watchGetCustomerCustomFieldsForOrganisationRequest() {
    yield takeLatest(
        getCustomerCustomFieldsForOrganisationRequestAction.type,
        handleGetCustomerCustomFieldsForOrganisationRequest
    );
}

function* watchGetCustomerFirstLetterValuesRequest() {
    yield takeLatest(
        getCustomerFirstLetterValuesRequestAction.type,
        handleGetCustomerFirstLetterValuesRequest
    );
}

function* watchGetCustomerFirstLetterValuesForOrganisationRequest() {
    yield takeLatest(
        getCustomerFirstLetterValuesForOrganisationRequestAction.type,
        getCustomerFirstLetterValuesForOrganisationRequest
    );
}

function* watchCustomerAssignUserRequest() {
    yield takeLatest(
        customerAssignUserRequestAction.type,
        handleCustomerAssignUserRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* customersSaga() {
    yield all([
        fork(watchGetCustomersRequest),
        fork(watchGetCustomerDataRequest),
        fork(watchGetOrganisationCustomerDataRequest),
        fork(watchSetCustomerSelectedIdRequest),
        fork(watchGetChildCustomersRequest), 
        fork(watchSetParentCustomerSelectedIdRequest),
        fork(watchGetChildCustomersForOrganisationRequest),
        fork(watchGetParentCustomerDataRequest),
        fork(watchGetCustomerInvoicesRequest),
        fork(watchGetOrganisationCustomerInvoicesRequest),
        fork(watchGetCustomerCreditsRequest),
        fork(watchGetOrganisationCustomerCreditsRequest),
        fork(watchGetCustomerPaymentsRequest),
        fork(watchGetOrganisationCustomerPaymentsRequest),
        fork(watchSaveCustomerVIPUpdateRequest),
        fork(watchSaveOrganisationCustomerVIPUpdateRequest),
        fork(watchBulkSaveCustomersVIPUpdateRequest),
        fork(watchGetCustomerConversationRequest),
        fork(watchGetOrganisationCustomerConversationRequest),
        fork(watchCustomerAddCommentRequest),
        fork(watchCustomerSendInformationRequest),
        fork(watchCustomerAssignUserRequest),
        fork(watchGetCustomerCustomFieldsForCompanyRequest),
        fork(watchGetCustomerCustomFieldsForOrganisationRequest),
        fork(watchGetCustomerFirstLetterValuesRequest),
        fork(watchOrganisationCustomerAddCommentRequest),
        fork(watchGetCustomersForOrganisationRequest),
        fork(watchGetCustomerFirstLetterValuesForOrganisationRequest),
        fork(watchCustomersFetchedForOrganisationReceived),
        fork(watchCustomerInvoicesFetchedForOrganisationReceived),
        fork(watchCustomerCreditsFetchedForOrganisationReceived),
        fork(watchCustomerPaymentsFetchedForOrganisationReceived),
        fork(watchCustomerConversationLinesFetchedForOrganisationReceived)
    ]);
}

export default customersSaga;